import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getMonthlyBilling, getBillingChecks, setBillingCheck, getBillableRdvDetail, getRdvCounts, channelLabel, BILLING_STATUS_LABELS, type ClientBilling, type Channel, type BillingStatus, type BillableRdv } from '../../lib/refonteApi';
import { useAuth } from '../../components/AuthProvider';
import ContractBadge from '../../components/refonte/ContractBadge';
import StatTile from '../../components/refonte/StatTile';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const eur = (n: number) => n.toLocaleString('fr-FR') + ' €';
const NEXT: Record<BillingStatus, BillingStatus> = { todo: 'invoiced', invoiced: 'sent', sent: 'todo' };
const STATUS_CLS: Record<BillingStatus, string> = {
  todo: 'bg-raise text-dim border-line-2',
  invoiced: 'bg-st-warn/10 text-st-warn border-st-warn/30',
  sent: 'bg-st-ok/10 text-st-ok border-st-ok/30',
};

// Facturation — clients ACTIFS uniquement. Deux jobs : (1) combien facturer (fixe + RDV validés ×
// prix pour l'hybride), (2) suivi Baptiste : qui a été facturé / envoyé, clic pour faire tourner.
const FacturationPage: React.FC = () => {
  const { profile } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState<ClientBilling[]>([]);
  const [tot, setTot] = useState({ totalFixed: 0, totalVariable: 0, total: 0 });
  const [byChannel, setByChannel] = useState<{ channel: Channel; total: number; count: number }[]>([]);
  const [checks, setChecks] = useState<Map<string, BillingStatus>>(new Map());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // Détail des RDV facturables d'un hybride (Baptiste : liste nom/entreprise à coller dans Tiime).
  const [openDetail, setOpenDetail] = useState<string | null>(null);
  const [detail, setDetail] = useState<BillableRdv[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const toggleDetail = async (clientId: string) => {
    if (openDetail === clientId) { setOpenDetail(null); return; }
    setOpenDetail(clientId); setDetailLoading(true); setCopied(false);
    try { setDetail(await getBillableRdvDetail(clientId, year, month)); } catch { setDetail([]); }
    setDetailLoading(false);
  };
  const copyDetail = () => {
    const txt = detail.map(d => [d.prospectName, d.company].filter(Boolean).join(' — ')).join('\n');
    navigator.clipboard.writeText(txt).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [r, ck] = await Promise.all([getMonthlyBilling(year, month), getBillingChecks(year, month).catch(() => new Map<string, BillingStatus>())]);
      setRows(r.rows); setTot({ totalFixed: r.totalFixed, totalVariable: r.totalVariable, total: r.total });
      setByChannel(r.byChannel); setChecks(ck);
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const shift = (d: number) => {
    let m = month + d, y = year;
    if (m < 1) { m = 12; y--; } if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const cycle = async (clientId: string) => {
    const cur = checks.get(clientId) ?? 'todo';
    const next = NEXT[cur];
    setChecks(prev => { const n = new Map(prev); n.set(clientId, next); return n; }); // optimiste
    await setBillingCheck(clientId, year, month, next, profile?.user_id).catch(() => load());
  };

  const statusOf = (id: string): BillingStatus => checks.get(id) ?? 'todo';
  const nTodo = rows.filter(r => statusOf(r.clientId) === 'todo').length;
  // Les RDV encore « qualifiés » (non validés) ne sont PAS facturés ce mois — le signaler ici
  // au lieu de laisser le CA variable silencieusement incomplet (audit UX 09/10, finding #3).
  const [nAValider, setNAValider] = useState(0);
  useEffect(() => { getRdvCounts().then(c => setNAValider(c.aValider)).catch(() => {}); }, []);
  const nSent = rows.filter(r => statusOf(r.clientId) === 'sent').length;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Facturation</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} aria-label="Mois précédent" className="w-8 h-8 rounded-lg border border-line-2 hover:border-ink">‹</button>
          <span className="font-mono text-[12px] text-dim capitalize min-w-[150px] text-center">{MOIS[month - 1]} {year}</span>
          <button onClick={() => shift(1)} aria-label="Mois suivant" className="w-8 h-8 rounded-lg border border-line-2 hover:border-ink">›</button>
        </div>
      </div>
      <p className="text-dim text-[13px] mb-5">Clients actifs uniquement · fixe + (RDV validés × prix/RDV) pour l'hybride · clique le statut pour suivre les factures (à facturer → facturée → envoyée)</p>

      {err && <div className="mb-4 text-[13px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2 font-mono">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <StatTile label="Fixe (abonnements)" value={eur(tot.totalFixed)} />
        <StatTile label="Variable (RDV validés)" value={eur(tot.totalVariable)} className="text-hook-rose" />
        <StatTile label="Total du mois" value={eur(tot.total)} />
        <StatTile label="À facturer" value={String(nTodo)} className={nTodo ? 'text-st-warn' : 'text-st-ok'} />
        {nAValider > 0 && (
          <Link to="/app/rdv-a-valider" className="self-center text-[12px] font-semibold text-st-warn hover:underline">
            ⚠ {nAValider} RDV qualifié{nAValider > 1 ? 's' : ''} en attente de validation — non facturé{nAValider > 1 ? 's' : ''} tant que non validé{nAValider > 1 ? 's' : ''} →
          </Link>
        )}
        <StatTile label="Envoyées" value={`${nSent}/${rows.length}`} className={nSent === rows.length && rows.length > 0 ? 'text-st-ok' : ''} />
      </div>

      {byChannel.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="font-mono text-[9px] uppercase tracking-widest text-faint self-center mr-1">Par canal</span>
          {byChannel.map(c => (
            <span key={c.channel} className="inline-flex items-center gap-2 border border-line rounded-lg px-2.5 py-1 text-[12px]">
              <span className="font-semibold">{channelLabel(c.channel)}</span>
              <span className="font-mono text-dim">{eur(c.total)}</span>
              <span className="font-mono text-[10px] text-faint">· {c.count}</span>
            </span>
          ))}
        </div>
      )}

      <div className="bg-panel border border-line rounded-card p-5">
        <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-4">Détail par client</h2>
        {loading ? <SkeletonRows count={6} /> : rows.length === 0 ? (
          <EmptyState title="Aucune facturation ce mois" hint="Aucun client actif avec un calibrage tarifé." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr>
                  {['Client', 'Formule', 'Fixe', 'RDV validés', 'Variable', 'Total', 'Suivi'].map((h, i) => (
                    <th key={h} className={`font-mono text-[9.5px] uppercase tracking-widest text-faint pb-2 ${i > 1 && i < 6 ? 'text-right' : 'text-left'} ${i === 6 ? 'text-center pl-3' : ''}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map(r => {
                  const st = statusOf(r.clientId);
                  const isVar = r.contractType === 'variable';
                  const open = openDetail === r.clientId;
                  return (
                    <React.Fragment key={r.clientId}>
                    <tr className="hover:bg-panel-2">
                      <td className="py-2.5 text-[13px] font-semibold"><Link to={`/app/clients/${r.clientId}`} className="hover:text-hook-rose" title="Ouvrir le dossier client (contrats)">{r.name}</Link></td>
                      <td className="py-2.5"><span className="font-mono text-[9px] text-dim mr-1.5">{channelLabel(r.channel)}</span><ContractBadge contractType={r.contractType} /></td>
                      <td className="py-2.5 text-right font-mono text-[12.5px]">{eur(r.fixed)}</td>
                      <td className="py-2.5 text-right font-mono text-[12.5px]">
                        {isVar
                          ? <button onClick={() => toggleDetail(r.clientId)} disabled={!r.rdvValidated} title="Voir le détail des RDV à facturer"
                              className={`font-bold ${r.rdvValidated ? 'text-hook-rose hover:underline cursor-pointer' : 'text-faint cursor-default'}`}>
                              {r.rdvValidated} <span className="font-normal text-dim">× {r.pricePerRdv}€</span>{r.rdvValidated ? <span className="ml-1 text-[10px]">{open ? '▴' : '▾'}</span> : ''}
                            </button>
                          : <span className="text-faint">—</span>}
                      </td>
                      <td className="py-2.5 text-right font-mono text-[12.5px] text-hook-rose">{r.variable ? eur(r.variable) : <span className="text-faint">—</span>}</td>
                      <td className="py-2.5 text-right font-mono text-[13px] font-bold">{eur(r.total)}</td>
                      <td className="py-2.5 pl-3 text-center">
                        <button onClick={() => cycle(r.clientId)} title="Clique pour passer au statut suivant"
                          className={`font-mono text-[10px] px-2 py-1 rounded-md border transition ${STATUS_CLS[st]} hover:brightness-95`}>
                          {st === 'sent' ? '✓ ' : st === 'invoiced' ? '◐ ' : '○ '}{BILLING_STATUS_LABELS[st]}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={7} className="bg-panel-2/50 px-3 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono text-[9px] uppercase tracking-widest text-faint">RDV qualifiés honorés à facturer · {MOIS[month - 1]} {year}</span>
                            <button onClick={copyDetail} disabled={!detail.length} className="font-mono text-[10px] px-2 py-1 rounded-md bg-hook-rose text-white disabled:opacity-40">{copied ? '✓ copié' : '⧉ copier pour Tiime'}</button>
                          </div>
                          {detailLoading ? <span className="text-[12px] text-dim">Chargement…</span> : detail.length === 0 ? <span className="text-[12px] text-faint">Aucun RDV facturable ce mois.</span> : (
                            <ol className="text-[12.5px] space-y-0.5 list-decimal pl-5">
                              {detail.map((d, i) => (
                                <li key={i}><span className="font-semibold">{d.prospectName || 'Prospect'}</span>{d.company && <span className="text-dim"> — {d.company}</span>}{d.jobTitle && <span className="text-faint text-[11px]"> · {d.jobTitle}</span>}</li>
                              ))}
                            </ol>
                          )}
                          <p className="font-mono text-[10px] text-faint mt-2">{detail.length} RDV × {r.pricePerRdv}€ = <b className="text-hook-rose">{eur(detail.length * (r.pricePerRdv || 0))}</b> · à facturer pour {r.name}</p>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-line-2">
                  <td className="pt-3 text-[12px] font-mono uppercase tracking-widest text-dim" colSpan={2}>Total</td>
                  <td className="pt-3 text-right font-mono text-[12.5px]">{eur(tot.totalFixed)}</td>
                  <td></td>
                  <td className="pt-3 text-right font-mono text-[12.5px] text-hook-rose">{eur(tot.totalVariable)}</td>
                  <td className="pt-3 text-right font-mono text-[14px] font-bold">{eur(tot.total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <p className="font-mono text-[10px] text-faint mt-4">
          Le prix/RDV et le fixe viennent du contrat de chaque client (onglet Contrats du dossier client). Le suivi (à facturer / facturée / envoyée) est mémorisé par mois — pensé pour le suivi de Baptiste.
        </p>
      </div>
    </div>
  );
};

export default FacturationPage;
